/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, spriteUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, RotateCcw, Search, Lock, GripVertical, Package, Map, ShoppingCart, AlertTriangle } from "lucide-react";
import AdminShopPanel from "@/components/AdminShopPanel";

const LEGENDARY_IDS = new Set([144, 145, 146, 150, 151]);

export default function AdminRoutesPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [filter, setFilter] = useState("");
  const [draftPokemon, setDraftPokemon] = useState(new Set());
  const [draftWeights, setDraftWeights] = useState({});
  const [draftLevels, setDraftLevels] = useState({ level_min: "", level_max: "", train_min_level: "", min_badges: "" });
  const [draftItems, setDraftItems] = useState({}); // {item_id: weight}
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("all"); // all | selected
  const [section, setSection] = useState("pokemon"); // pokemon | items | settings
  const [view, setView] = useState("routes"); // routes | shop
  const [resetOpen, setResetOpen] = useState(false);
  const dragIdxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/locations");
      setData(data);
      if (!selectedLoc && data.locations.length) {
        setSelectedLoc(data.locations[0].id);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Accès refusé");
    }
  }, [selectedLoc]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data || !selectedLoc) return;
    const loc = data.locations.find((l) => l.id === selectedLoc);
    if (loc) {
      setDraftPokemon(new Set(loc.pokemon));
      setDraftWeights({ ...loc.weights });
      setDraftLevels({
        level_min: loc.level_min ?? "",
        level_max: loc.level_max ?? "",
        train_min_level: loc.train_min_level ?? "",
        min_badges: loc.min_badges ?? "",
      });
      const itemsMap = {};
      (loc.item_pool || []).forEach((p) => { itemsMap[p.item_id] = p.weight; });
      setDraftItems(itemsMap);
    }
  }, [selectedLoc, data]);

  const currentLoc = useMemo(
    () => data?.locations.find((l) => l.id === selectedLoc) || null,
    [data, selectedLoc]
  );

  const isDirty = useMemo(() => {
    if (!currentLoc) return false;
    const cur = new Set(currentLoc.pokemon);
    if (cur.size !== draftPokemon.size) return true;
    for (const id of cur) if (!draftPokemon.has(id)) return true;
    for (const [k, v] of Object.entries(draftWeights)) {
      if ((currentLoc.weights[k] ?? 10) !== v) return true;
    }
    for (const fld of ["level_min", "level_max", "train_min_level", "min_badges"]) {
      const curV = currentLoc[fld] ?? "";
      const dr = draftLevels[fld] === "" ? "" : Number(draftLevels[fld]);
      if (curV !== dr) return true;
    }
    const curItems = {};
    (currentLoc.item_pool || []).forEach((p) => { curItems[p.item_id] = p.weight; });
    const keysA = Object.keys(curItems).sort();
    const keysB = Object.keys(draftItems).sort();
    if (keysA.length !== keysB.length || keysA.some((k, i) => k !== keysB[i])) return true;
    for (const k of keysA) if (curItems[k] !== draftItems[k]) return true;
    return false;
  }, [currentLoc, draftPokemon, draftWeights, draftLevels, draftItems]);

  const togglePid = (pid) => {
    setDraftPokemon((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) {
        next.delete(pid);
        setDraftWeights((w) => { const c = { ...w }; delete c[String(pid)]; return c; });
      } else {
        next.add(pid);
        setDraftWeights((w) => ({ ...w, [String(pid)]: w[String(pid)] ?? 10 }));
      }
      return next;
    });
  };

  const updateWeight = (pid, value) => {
    const v = Math.max(0, Math.min(100, parseInt(value || "0", 10)));
    setDraftWeights((w) => ({ ...w, [String(pid)]: v }));
  };

  const toggleItem = (item_id) => {
    setDraftItems((prev) => {
      if (item_id in prev) {
        const c = { ...prev }; delete c[item_id]; return c;
      }
      return { ...prev, [item_id]: 10 };
    });
  };

  const updateItemWeight = (item_id, v) => {
    const w = Math.max(0, Math.min(999, parseInt(v || "0", 10)));
    setDraftItems((prev) => ({ ...prev, [item_id]: w }));
  };

  const save = async () => {
    if (!currentLoc) return;
    setBusy(true);
    try {
      const payload = {
        pokemon: [...draftPokemon].sort((a, b) => a - b),
        weights: draftWeights,
        item_pool: Object.entries(draftItems).map(([item_id, weight]) => ({ item_id, weight })),
      };
      for (const fld of ["level_min", "level_max", "train_min_level", "min_badges"]) {
        if (draftLevels[fld] !== "") payload[fld] = Number(draftLevels[fld]);
      }
      await api.put(`/admin/locations/${currentLoc.id}`, payload);
      toast.success(`« ${currentLoc.name} » sauvegardé.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!currentLoc) return;
    if (!window.confirm(`Réinitialiser « ${currentLoc.name} » et supprimer l'override ?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/locations/${currentLoc.id}/override`);
      toast.success("Override supprimé.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // ---- Drag-n-drop reorder ----
  const onDragStart = (idx) => () => { dragIdxRef.current = idx; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragIdxRef.current;
    if (from === null || from === idx) return;
    const next = [...data.locations];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setData({ ...data, locations: next });
    dragIdxRef.current = null;
    try {
      await api.put("/admin/locations/order", { order: next.map((l) => l.id) });
      toast.success("Ordre sauvegardé.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      await load();
    }
  };

  if (!user) return null;
  if (!data) return <div className="font-mono text-xs text-[#7A7A7A]">Chargement du panneau admin…</div>;

  const allIds = data.all_pokemon_ids || [];
  const allItems = data.all_items || [];
  const filtered = filter
    ? allIds.filter((pid) => pid.toString().includes(filter.trim()))
    : allIds;

  return (
    <div data-testid="admin-routes-page">
      <div className="mb-4 flex items-start gap-3">
        <div className="stamp" style={{ background: "#8C2A2A", color: "#FCF9F2" }}>ADMIN</div>
        <div className="flex-1">
          <div className="overline">Panneau Professeur Admin</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">
            {view === "routes" ? "Édition des routes" : "Édition de la boutique"}
          </h1>
          <p className="text-xs text-[#4A4A4A] mt-2 max-w-2xl">
            {view === "routes"
              ? "Glisse-dépose les routes pour les réordonner. Pour chaque zone : Pokémon (poids 1-100), objets trouvables et réglages de difficulté."
              : "Ajuste les prix et les seuils de dresseurs pour équilibrer la boutique."}
          </p>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            data-testid="admin-view-routes"
            onClick={() => setView("routes")}
            className={`btn-ink text-[11px] ${view === "routes" ? "btn-leaf" : ""}`}
          >
            <Map size={11}/> Routes
          </button>
          <button
            data-testid="admin-view-shop"
            onClick={() => setView("shop")}
            className={`btn-ink text-[11px] ${view === "shop" ? "btn-leaf" : ""}`}
          >
            <ShoppingCart size={11}/> Boutique
          </button>
          <button
            data-testid="admin-reset-btn"
            onClick={() => setResetOpen(true)}
            className="btn-ink btn-accent text-[11px]"
            title="Réinitialise toutes les données de jeu (dresseurs, PC, Pokédex, argent, inventaires) — conserve les comptes"
          >
            <AlertTriangle size={11}/> Reset comptes
          </button>
        </div>
      </div>

      {view === "shop" ? (
        <AdminShopPanel/>
      ) : (
      <div className="grid grid-cols-12 gap-4">
        {/* Routes list */}
        <aside className="col-span-12 lg:col-span-3 panel p-2 max-h-[700px] overflow-auto" data-testid="admin-route-list">
          {data.locations.map((r, idx) => {
            const isSel = r.id === selectedLoc;
            return (
              <div
                key={r.id}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={onDrop(idx)}
                data-testid={`admin-route-${r.id}`}
                className={`w-full text-left p-2 mb-1 border flex items-center gap-1 cursor-move ${isSel ? "border-[#B87333] bg-[#FCF9F2]" : "border-transparent hover:bg-[#FCF9F2]"}`}
                onClick={() => setSelectedLoc(r.id)}
              >
                <GripVertical size={12} className="text-[#7A7A7A] shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-base leading-tight truncate">{r.name}</div>
                  <div className="font-mono text-[10px] text-[#4A4A4A] mt-0.5 flex items-center gap-2">
                    <span>Diff {r.difficulty}</span>
                    <span>·</span>
                    <span>{r.pokemon.length} Pkmn</span>
                    {(r.item_pool && r.item_pool.length > 0) && <span className="text-[#B87333]">●obj</span>}
                    {r.min_badges > 0 && <span className="ml-auto"><Lock size={9} className="inline"/> {r.min_badges}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </aside>

        {/* Editor */}
        <section className="col-span-12 lg:col-span-9 panel p-4" data-testid="admin-editor">
          {currentLoc && (
            <>
              <div className="flex items-start justify-between gap-3 border-b border-[#2B2B2B] pb-3 mb-3">
                <div>
                  <div className="overline">Route</div>
                  <h2 className="font-serif-display text-2xl md:text-3xl leading-none mt-1">{currentLoc.name}</h2>
                  <p className="text-[11px] font-mono text-[#4A4A4A] mt-1">
                    Difficulté {currentLoc.difficulty} · {draftPokemon.size} Pokémon · {Object.keys(draftItems).length} objets
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-end shrink-0">
                  <button data-testid="admin-reset" className="btn-ink text-[11px]" onClick={reset} disabled={busy}>
                    <RotateCcw size={11}/> Réinitialiser
                  </button>
                  <button data-testid="admin-save" className={`btn-ink btn-leaf text-[11px] ${!isDirty ? "opacity-50" : ""}`} onClick={save} disabled={busy || !isDirty}>
                    <Save size={11}/> Sauvegarder
                  </button>
                </div>
              </div>

              {/* Section tabs */}
              <div className="flex gap-1 mb-3">
                {[
                  { id: "pokemon", label: "Pokémon" },
                  { id: "items", label: "Objets trouvables" },
                  { id: "settings", label: "Réglages" },
                ].map((s) => (
                  <button
                    key={s.id}
                    data-testid={`admin-section-${s.id}`}
                    onClick={() => setSection(s.id)}
                    className={`btn-ink text-[10px] ${section === s.id ? "btn-leaf" : ""}`}
                  >{s.label}</button>
                ))}
              </div>

              {section === "settings" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3 panel p-3">
                  <LevelInput label="Niv. min Pokémon" testid="admin-level-min" value={draftLevels.level_min} onChange={(v) => setDraftLevels((d) => ({ ...d, level_min: v }))}/>
                  <LevelInput label="Niv. max Pokémon" testid="admin-level-max" value={draftLevels.level_max} onChange={(v) => setDraftLevels((d) => ({ ...d, level_max: v }))}/>
                  <LevelInput label="Niv. min entraînement" testid="admin-train-min" value={draftLevels.train_min_level} onChange={(v) => setDraftLevels((d) => ({ ...d, train_min_level: v }))} hint="Sous ce seuil, échec accru"/>
                  <LevelInput label="Badges requis" testid="admin-min-badges" value={draftLevels.min_badges} onChange={(v) => setDraftLevels((d) => ({ ...d, min_badges: v }))} max={8} hint="0 à 8"/>
                </div>
              )}

              {section === "items" && (
                <div>
                  <p className="text-[11px] font-mono text-[#4A4A4A] mb-2">
                    <Package size={11} className="inline mr-1"/>
                    Si la liste est vide, la table d&apos;exploration par défaut s&apos;applique. Le poids détermine la rareté (10 = neutre, 50 = commun, 2 = rare).
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {allItems.map((it) => {
                      const checked = it.item_id in draftItems;
                      return (
                        <div
                          key={it.item_id}
                          data-testid={`admin-item-${it.item_id}`}
                          className={`panel p-2 flex items-center gap-2 cursor-pointer ${checked ? "border-[#2E4F32] bg-[#EDF3EC]" : "border-[#2B2B2B]"}`}
                          onClick={() => toggleItem(it.item_id)}
                        >
                          <img src={it.sprite} alt={it.name} className={`pixelated w-8 h-8 shrink-0 ${!checked ? "opacity-50" : ""}`}/>
                          <div className="flex-1 min-w-0">
                            <div className="font-serif-display text-sm leading-tight truncate">{it.name}</div>
                            <div className="font-mono text-[9px] text-[#4A4A4A]">{it.type} · {it.sell_value}₽</div>
                          </div>
                          {checked && (
                            <input
                              type="number"
                              min="0"
                              max="999"
                              value={draftItems[it.item_id]}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => updateItemWeight(it.item_id, e.target.value)}
                              className="w-12 text-center font-mono text-[10px] border border-[#2B2B2B] bg-[#FCF9F2] px-1 py-0.5"
                              data-testid={`admin-item-weight-${it.item_id}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {section === "pokemon" && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1">
                      <button onClick={() => setTab("all")} className={`btn-ink text-[10px] ${tab === "all" ? "btn-leaf" : ""}`}>Tous (151)</button>
                      <button onClick={() => setTab("selected")} className={`btn-ink text-[10px] ${tab === "selected" ? "btn-leaf" : ""}`}>Sélection ({draftPokemon.size})</button>
                    </div>
                    <div className="relative flex-1 max-w-xs ml-auto">
                      <Search size={12} className="absolute left-2 top-2.5 text-[#7A7A7A]"/>
                      <input data-testid="admin-search" type="text" placeholder="Filtrer par ID..." value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full pl-7 pr-2 py-1.5 border border-[#2B2B2B] bg-[#FCF9F2] font-mono text-xs"/>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2">
                    {(tab === "selected" ? [...draftPokemon].sort((a, b) => a - b) : filtered).map((pid) => {
                      const checked = draftPokemon.has(pid);
                      const isLeg = LEGENDARY_IDS.has(pid);
                      return (
                        <div
                          key={pid}
                          data-testid={`admin-pkmn-${pid}`}
                          className={`panel p-2 flex flex-col items-center text-center cursor-pointer ${checked ? "border-[#2E4F32] bg-[#EDF3EC]" : "border-[#2B2B2B]"} ${isLeg ? "ring-1 ring-[#B87333]" : ""}`}
                          onClick={() => togglePid(pid)}
                        >
                          <img src={spriteUrl(pid)} alt={`#${pid}`} className={`pixelated w-10 h-10 ${!checked ? "opacity-50" : ""}`}/>
                          <div className="font-mono text-[10px] mt-1">#{pid}{isLeg ? " 🌟" : ""}</div>
                          {checked && (
                            <input
                              type="number" min="0" max="100"
                              value={draftWeights[String(pid)] ?? 10}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => updateWeight(pid, e.target.value)}
                              className="mt-1 w-12 text-center font-mono text-[10px] border border-[#2B2B2B] bg-[#FCF9F2] px-1 py-0.5"
                              data-testid={`admin-weight-${pid}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
      )}

      {resetOpen && <ResetAccountsModal onClose={() => setResetOpen(false)} onDone={load}/>}
    </div>
  );
}

function ResetAccountsModal({ onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const canReset = confirm === "RESET";
  const run = async () => {
    if (!canReset) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-accounts", { confirm: "RESET" });
      const s = data.stats || {};
      toast.success(
        `Comptes réinitialisés · ${s.users_reset || 0} utilisateurs · ${s.trainers || 0} dresseurs, ${s.pc_pokemon || 0} PC, ${s.events || 0} events supprimés.`
      );
      onClose();
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(26,26,26,0.6)", zIndex: 90 }}
      onClick={onClose}
      data-testid="admin-reset-modal"
    >
      <div className="panel-strong p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} className="text-[#8C2A2A]"/>
          <h2 className="font-serif-display text-2xl leading-none">Réinitialisation totale</h2>
        </div>
        <p className="text-sm text-[#1A1A1A] mb-2">
          Cette action va effacer <strong>toutes les données de jeu des utilisateurs non-admin</strong> :
        </p>
        <ul className="text-xs font-mono text-[#4A4A4A] list-disc pl-5 mb-3 space-y-0.5">
          <li>Tous les dresseurs recrutés</li>
          <li>Tous les Pokémon (équipes + PC)</li>
          <li>Tous les Pokédex, inventaires et argent</li>
          <li>Tout le journal d&apos;activité</li>
        </ul>
        <p className="text-xs text-[#4A4A4A] mb-2">
          Sont <strong>conservés</strong> : comptes utilisateurs, configurations admin (routes, boutique), <strong>et l&apos;intégralité des données de ton compte admin</strong> (dresseurs, PC, Pokédex, régions débloquées, argent).
        </p>
        <div className="border-l-2 border-[#8C2A2A] bg-[#FAEDED] px-3 py-2 mb-3">
          <p className="text-[11px] font-mono text-[#8C2A2A]">
            🛡️ Ton compte admin (<em>paratre@gmail.com</em>) est <strong>protégé</strong> : la réinitialisation ne touche jamais tes propres sauvegardes.
          </p>
        </div>
        <p className="text-xs text-[#4A4A4A] mb-3">
          Chaque compte non-admin repart avec 500 ₽ et 20 Poké Balls, région Kanto uniquement.
        </p>
        <div className="mb-3">
          <div className="overline text-[10px] mb-1">Tape <span className="text-[#8C2A2A]">RESET</span> pour confirmer</div>
          <input
            data-testid="admin-reset-confirm-input"
            className="w-full font-mono text-sm border border-[#2B2B2B] bg-[#FCF9F2] px-2 py-2"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn-ink" onClick={onClose} disabled={busy} data-testid="admin-reset-cancel">
            Annuler
          </button>
          <button
            className="btn-ink btn-accent"
            onClick={run}
            disabled={!canReset || busy}
            data-testid="admin-reset-confirm"
          >
            {busy ? "Réinitialisation…" : "Tout réinitialiser"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LevelInput({ label, value, onChange, testid, hint, max = 100 }) {
  return (
    <div>
      <div className="overline text-[9px] mb-1">{label}</div>
      <input
        type="number" min="0" max={max}
        value={value}
        placeholder="auto"
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-mono text-xs border border-[#2B2B2B] bg-[#FCF9F2] px-2 py-1"
        data-testid={testid}
      />
      {hint && <div className="font-mono text-[9px] text-[#7A7A7A] mt-1">{hint}</div>}
    </div>
  );
}
